import { Link, useParams } from "react-router-dom";
import { getNewsById, getRelatedNews, formatDate } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const item = getNewsById(id);

  usePageMeta({
    title: item ? `${item.title} - 日本伦理资讯动态` : "资讯未找到 - 日本伦理",
    description: item ? item.excerpt : "该资讯不存在或已下线，可返回资讯列表继续浏览。",
    keywords: item ? item.tags.join(",") : "日本伦理影视资讯",
  });

  if (!item) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center px-4 pt-24">
        <div className="w-full max-w-lg rounded-lg border border-dashed border-background-700 bg-background-900 px-6 py-14 text-center">
          <i className="ri-article-line text-3xl text-accent-500" aria-hidden="true"></i>
          <h1 className="mt-4 text-xl font-medium text-foreground-950">资讯不存在</h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            该篇资讯可能已被移除，或链接有误。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回资讯列表
          </Link>
        </div>
      </section>
    );
  }

  const related = getRelatedNews(item, 3);

  return (
    <>
      <article>
        <header className="border-b border-background-800 bg-background-900">
          <div className="mx-auto max-w-4xl px-4 pb-10 pt-24 md:px-8 md:pb-12 md:pt-32">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to="/news" className="transition-colors hover:text-accent-400">
                资讯动态
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="truncate text-foreground-800">{item.category}</span>
            </nav>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-background-950">
                {item.category}
              </span>
              <span className="font-label tracking-[0.14em] text-foreground-500">
                {formatDate(item.date)}
              </span>
              <span className="text-foreground-500">约 6 分钟阅读</span>
            </div>

            <h1 className="mt-4 text-2xl font-semibold leading-snug tracking-tight text-foreground-950 md:text-4xl md:leading-tight">
              {item.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {item.excerpt}
            </p>
          </div>
        </header>

        <div className="mx-auto max-w-4xl px-4 py-10 md:px-8 md:py-14">
          <div className="overflow-hidden rounded-lg border border-background-800">
            <img
              src={item.thumbnail}
              alt={`${item.title}配图`}
              title={`${item.title} - 日本伦理资讯`}
              className="aspect-[16/9] w-full object-cover object-center"
            />
          </div>

          <div className="mt-10 space-y-6">
            {item.content.map((paragraph, index) => (
              <div key={paragraph.slice(0, 14)}>
                <p className="text-sm leading-7 text-foreground-600 md:text-[15px] md:leading-8">
                  {paragraph}
                </p>
                {index === 1 && (
                  <blockquote className="mt-6 rounded-lg border-l-2 border-accent-500 bg-background-900 px-5 py-4">
                    <p className="text-sm leading-relaxed text-foreground-700">
                      伦理叙事的力量不来自结论，而来自它让观众不得不自己完成判断。
                    </p>
                  </blockquote>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-background-700 px-3 py-1 text-xs text-foreground-600"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-800 pt-6">
            <Link
              to="/news"
              className="flex items-center gap-2 rounded-md border border-background-600 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              <span className="whitespace-nowrap">返回资讯列表</span>
            </Link>
            <p className="text-xs text-foreground-500">
              本文为虚构演示内容，不代表任何真实媒体观点。
            </p>
          </div>
        </div>
      </article>

      <section className="border-t border-background-800 bg-background-900">
        <div className="mx-auto max-w-4xl px-4 py-12 md:px-8 md:py-16">
          <div className="flex items-end justify-between border-b border-background-800 pb-4">
            <h2 className="text-lg font-semibold text-foreground-950">相关文章推荐</h2>
            <span className="text-xs text-foreground-500">按栏目与标签匹配</span>
          </div>
          <ul className="mt-2">
            {related.map((entry) => (
              <li key={entry.id} className="border-b border-background-800 last:border-b-0">
                <Link
                  to={`/news/${entry.id}`}
                  className="group flex items-start justify-between gap-4 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm leading-snug text-foreground-900 transition-colors group-hover:text-accent-400">
                      {entry.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">
                      {entry.category} · {entry.date}
                    </p>
                  </div>
                  <i
                    className="ri-arrow-right-up-line mt-1 shrink-0 text-foreground-500 transition-colors group-hover:text-accent-400"
                    aria-hidden="true"
                  ></i>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}